import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2, Mail, Sparkles, AlertTriangle, Info, TrendingUp } from "lucide-react";
import {
  fetchTaxReadiness, callTaxCenter, fetchYearEndDashboard, maskTin, readinessLabel, money,
  type TaxReadinessRow, type ReadinessState, type YearEndDashboard,
} from "@/lib/taxCompliance";

export default function TaxCenterPage() {
  return (
    <AdminShell>
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
        <header className="space-y-1">
          <h1 className="text-2xl font-heading">Tax Center</h1>
          <p className="text-sm text-muted-foreground">
            Year-end tax management for paid instructors. W-9 compliance, threshold tracking,
            and 1099 preparation. Full Tax IDs stay encrypted; only the last four digits appear here.
          </p>
        </header>
        <Tabs defaultValue="dashboard" className="space-y-4">
          <TabsList>
            <TabsTrigger value="dashboard">Year-End Dashboard</TabsTrigger>
            <TabsTrigger value="compliance">W-9 Compliance</TabsTrigger>
            <TabsTrigger value="prep">1099 Preparation</TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard"><DashboardTab /></TabsContent>
          <TabsContent value="compliance"><ComplianceTab /></TabsContent>
          <TabsContent value="prep"><PrepTab /></TabsContent>
        </Tabs>
      </div>
    </AdminShell>
  );
}

function DashboardTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState<YearEndDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { setData(await fetchYearEndDashboard(year)); } catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [year]);

  if (loading || !data) return <p className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</p>;
  const c = data.counts;

  return (
    <div className="space-y-4">
      {data.alerts.length > 0 && (
        <div className="space-y-2">
          {data.alerts.map((a, i) => (
            <Alert key={i} variant={a.level === "warn" ? "destructive" : "default"}>
              {a.level === "warn" ? <AlertTriangle className="h-4 w-4" /> : <Info className="h-4 w-4" />}
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>{a.message}</span>
                {a.href && <a href={a.href} className="text-xs underline">{a.action ?? "Open"}</a>}
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Tax year:</span>
        <Input type="number" value={year} min={2020} max={now.getFullYear()} onChange={(e) => setYear(Number(e.target.value) || year)} className="w-24" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total instructors" value={c.total} />
        <KpiCard label="🟢 W-9 complete" value={c.w9_complete} />
        <KpiCard label="🟠 Awaiting verification" value={c.pending_review} />
        <KpiCard label="🔴 Missing W-9" value={c.missing_w9} tone={c.missing_w9 > 0 ? "warn" : undefined} />
        <KpiCard label={`Est. 1099 recipients (${data.tax_year})`} value={c.ready_for_1099} icon={<TrendingUp className="h-3 w-3" />} />
        <KpiCard label="Ready to file" value={data.drafts.approved} />
        <KpiCard label="Drafts in progress" value={data.drafts.drafts} />
        <KpiCard label="🔴 Action required" value={c.action_needed} tone={c.action_needed > 0 ? "warn" : undefined} />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Threshold crossings (last 7 days)</CardTitle></CardHeader>
        <CardContent>
          {data.threshold_crossers_7d.length === 0 ? (
            <p className="text-sm text-muted-foreground">No instructors have crossed the $600 threshold in the past week.</p>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Instructor</TableHead><TableHead>Crossed on</TableHead><TableHead className="text-right">Running total</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.threshold_crossers_7d.map((r) => (
                  <TableRow key={r.instructor_id}>
                    <TableCell>{r.preferred_name ?? r.full_name}</TableCell>
                    <TableCell className="text-sm">{new Date(r.crossed_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right font-medium">{money(r.running_total_cents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, icon, tone }: { label: string; value: number; icon?: React.ReactNode; tone?: "warn" }) {
  return (
    <div className={`p-3 rounded border ${tone === "warn" ? "border-destructive/40 bg-destructive/5" : "border-border"}`}>
      <div className="text-xs text-muted-foreground flex items-center gap-1">{icon}{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function ComplianceTab() {
  const [rows, setRows] = useState<TaxReadinessRow[]>([]);
  const [loading, setLoading] = useState(true);
  const initialFilter = ((): ReadinessState | "all" => {
    const q = new URLSearchParams(window.location.search).get("filter");
    if (q === "ready" || q === "not_yet_required" || q === "pending" || q === "action_needed" || q === "missing") return q;
    return "all";
  })();
  const [filter, setFilter] = useState<ReadinessState | "all">(initialFilter);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows(await fetchTaxReadiness()); } catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    total: rows.length,
    ready: rows.filter((r) => r.readiness_state === "ready").length,
    not_yet_required: rows.filter((r) => r.readiness_state === "not_yet_required").length,
    pending: rows.filter((r) => r.readiness_state === "pending").length,
    action_needed: rows.filter((r) => r.readiness_state === "action_needed").length,
    missing: rows.filter((r) => r.readiness_state === "missing").length,
  }), [rows]);

  const visible = useMemo(() => rows.filter((r) => {
    if (filter !== "all" && r.readiness_state !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (r.full_name ?? "").toLowerCase().includes(s)
        || (r.preferred_name ?? "").toLowerCase().includes(s)
        || (r.email ?? "").toLowerCase().includes(s)
        || (r.legal_name ?? "").toLowerCase().includes(s);
    }
    return true;
  }), [rows, filter, search]);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const bulkRemind = async () => {
    if (selected.size === 0) return;
    setSending(true);
    try {
      const r = await callTaxCenter({ action: "send_reminder", instructor_ids: [...selected] });
      const ok = r.results?.filter((x: any) => x.ok).length ?? 0;
      toast.success(`Sent ${ok}/${selected.size} reminders`);
      setSelected(new Set());
    } catch (e) { toast.error((e as Error).message); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard label="Total" value={counts.total} active={filter === "all"} onClick={() => setFilter("all")} />
        <StatCard label="🟢 Ready" value={counts.ready} active={filter === "ready"} onClick={() => setFilter("ready")} />
        <StatCard label="🟡 Not yet required" value={counts.not_yet_required} active={filter === "not_yet_required"} onClick={() => setFilter("not_yet_required")} />
        <StatCard label="🟠 Pending review" value={counts.pending} active={filter === "pending"} onClick={() => setFilter("pending")} />
        <StatCard label="🔴 Action required" value={counts.action_needed} active={filter === "action_needed"} onClick={() => setFilter("action_needed")} />
        <StatCard label="🔴 Missing W-9" value={counts.missing} active={filter === "missing"} onClick={() => setFilter("missing")} />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-3">
            <Input placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />
            <div className="ml-auto flex gap-2">
              {selected.size > 0 && (
                <Button size="sm" onClick={bulkRemind} disabled={sending}>
                  {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  Send reminder ({selected.size})
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={load}>Refresh</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
          ) : visible.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No instructors match this filter.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Instructor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Tax ID</TableHead>
                  <TableHead className="text-right">YTD paid</TableHead>
                  <TableHead>Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((r) => {
                  const l = readinessLabel(r.readiness_state);
                  return (
                    <TableRow key={r.instructor_id}>
                      <TableCell>
                        {(r.readiness_state === "missing" || r.readiness_state === "action_needed") && (
                          <Checkbox checked={selected.has(r.instructor_id)} onCheckedChange={() => toggle(r.instructor_id)} />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{r.preferred_name ?? r.full_name}</div>
                        <div className="text-xs text-muted-foreground">{r.email ?? "no email"}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline"><span className="mr-1">{l.icon}</span>{l.text}</Badge></TableCell>
                      <TableCell className="text-sm">
                        {r.collection_method === "online" ? "Online"
                          : r.collection_method === "pdf_upload" ? "PDF"
                          : r.collection_method === "external_provider" ? "External"
                          : "—"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{maskTin(r.tin_last4, r.tin_type)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {money(r.ytd_earnings_cents)}
                        {r.threshold_met && <span className="ml-1 text-[10px] text-amber-600">✓ threshold</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {r.completed_at ? new Date(r.completed_at).toLocaleDateString() : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PrepTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear() - (now.getMonth() < 2 ? 1 : 0));
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await callTaxCenter({ action: "list_1099_ready", tax_year: year });
      setData(r);
    } catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [year]);

  const generate = async () => {
    if (!confirm(`Generate draft 1099s for ${year}? Nothing will be sent — this only creates drafts you can review.`)) return;
    setGenerating(true);
    try {
      const r = await callTaxCenter({ action: "generate_1099_drafts", tax_year: year });
      toast.success(`Generated ${r.created_or_updated?.length ?? 0} drafts`);
      load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setGenerating(false); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">1099 Preparation</CardTitle>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm">Tax year:</span>
              <Input type="number" value={year} min={2020} max={now.getFullYear()} onChange={(e) => setYear(Number(e.target.value) || year)} className="w-24" />
              <Button size="sm" onClick={generate} disabled={generating || !data || data.ready?.length === 0}>
                {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Generate drafts
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</p>
          ) : !data ? null : (
            <div className="space-y-5">
              <Section title={`🟢 Ready for 1099 (${data.ready?.length ?? 0})`} rows={data.ready ?? []} showBlockedReason={false} />
              <Section title={`🔴 Blocked — missing verified W-9 (${data.blocked?.length ?? 0})`} rows={data.blocked ?? []} showBlockedReason={true} />
              <Section title={`⚪ Under $${(data.threshold_cents / 100).toFixed(0)} threshold (${data.under_threshold?.length ?? 0})`} rows={data.under_threshold ?? []} showBlockedReason={false} muted />
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertDescription className="text-xs">
          Nothing is filed or sent automatically. Generating drafts stores a reviewable record per instructor.
          Full Tax IDs are only decrypted in memory when an admin approves the draft with "Include full TIN" — the action is logged.
        </AlertDescription>
      </Alert>
    </div>
  );
}

function Section({ title, rows, showBlockedReason, muted }: { title: string; rows: any[]; showBlockedReason: boolean; muted?: boolean }) {
  if (rows.length === 0) return <div className={`text-sm ${muted ? "text-muted-foreground" : ""}`}>{title} — none</div>;
  return (
    <div>
      <h4 className={`text-sm font-medium mb-2 ${muted ? "text-muted-foreground" : ""}`}>{title}</h4>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Instructor</TableHead>
            <TableHead>Legal name (W-9)</TableHead>
            <TableHead className="text-right">Total paid</TableHead>
            <TableHead>Tax ID</TableHead>
            {showBlockedReason && <TableHead>Reason</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r: any) => (
            <TableRow key={r.instructor_id}>
              <TableCell>{r.preferred_name ?? r.full_name}</TableCell>
              <TableCell className="text-sm">{r.legal_name ?? "—"}</TableCell>
              <TableCell className="text-right font-medium">${(r.total_cents / 100).toFixed(2)}</TableCell>
              <TableCell className="font-mono text-xs">{maskTin(r.tin_last4, r.tin_type)}</TableCell>
              {showBlockedReason && <TableCell className="text-xs">{r.w9_status === "pending_review" ? "W-9 pending review" : r.w9_status === "rejected" ? "W-9 rejected" : "No W-9 on file"}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function StatCard({ label, value, active, onClick }: { label: string; value: number; active?: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`p-3 rounded border text-left transition ${active ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </button>
  );
}
